import React, { useState, useRef, useEffect, useContext } from 'react';
import iconMic from '../../assets/icon-microphone.svg';
import iconEnter from '../../assets/icon-enter-arrow.svg';
import microphoneActiveIcon from '../../assets/microphoneActiveIcon.svg';
import iconStar from '../../assets/icon-stars-streamline.svg';
import ferdyIcon from '../../assets/ferdyIcon.png';
import iconSearch from '../../assets/icon-search.svg';
import iconAttach from '../../assets/icon-paperclip.svg';
import iconImg1 from '../../assets/icon-test-tube.svg';
import iconImg2 from '../../assets/icon-atom.svg';
import { GoArrowUpRight } from 'react-icons/go';
import { IoIosArrowForward } from 'react-icons/io';
import { SearchBlockStyled, SearchItem } from './SearchBlock.styles';
import useForm from '../Form/useForm';
import Form from '../Form/Form';
import Field from '../Field';
import ModalContainer from '../ModalContainer';
import UploadFileModal from '../UploadFileModal';
import sessionsService from '../../services/sessionsServices';
import chatService from '../../services/chatService';
import { useNavigate } from 'react-router-dom';
import { TemporaryChatContext } from '../../context/temporaryChatContext';
import agentService from '../../services/agentService';
import Loaders from '../Loaders';
import { parsePossiblyBrokenJson } from '../../helpers/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import teamService from '../../services/teamService';

const SearchBlock = ({ createSession, sessionId, noMargin }) => {
  const [form] = useForm();
  const [showEnter, setShowEnter] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const [webOpen, setWebOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoDirection, setAutoDirection] = useState('down');
  const [webDirection, setWebDirection] = useState('down');
  const queryClient = useQueryClient();
  const {
    updateUserChat,
    updateWebSearch,
    webSearch,
    updateBotChat,
    setChatmessages,
    sessionID,
    setSessionID,
    updateChatSource,
    selectedAgent,
    setSelectedAgent,
    updateAgent,
    toggleRef,
    fakeAiLoader,
    fakeAiLoaderClear,
    setSessionAgent,
    updateChatLoaderMessage,
    updateToolImages,
  } = useContext(TemporaryChatContext);

  const autoRef = useRef(null);
  const webRef = useRef(null);
  const autoDropdownRef = useRef(null);
  const webDropdownRef = useRef(null);
  const navigate = useNavigate();
  const [teamData, setTeamData] = useState(null);

  const searchQuery = null;

  const { _loading, _data } = teamService.GetTeamList(searchQuery, fetch);

  useEffect(() => {
    if (_data) {
      setTeamData(_data?.teams);
    }
  }, [_data]);

  useEffect(() => {
    if (autoOpen) {
      adjustDropdownDirection(autoRef, autoDropdownRef, setAutoDirection);
    }
  }, [autoOpen]);

  useEffect(() => {
    if (webOpen) {
      adjustDropdownDirection(webRef, webDropdownRef, setWebDirection);
    }
  }, [webOpen]);

  const adjustDropdownDirection = (buttonRef, dropdownRef, setDirection) => {
    if (!buttonRef.current || !dropdownRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current.offsetHeight;
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      setDirection('up');
    } else {
      setDirection('down');
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (autoRef.current && !autoRef.current.contains(event.target)) {
        setAutoOpen(false);
      }
      if (webRef.current && !webRef.current.contains(event.target)) {
        setWebOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [isListening, setIsListening] = useState(false);

  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert('Your browser does not support Speech Recognition.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = event => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      form.setFieldsValue({
        search: finalTranscript + interimTranscript,
      });
    };

    recognition.onerror = event => {
      console.error('Speech recognition error:', event.error);
    };

    recognitionRef.current = recognition;
  }, []);

  const handleStart = () => {
    if (recognitionRef.current) {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const handleStop = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mutationCreateSession = useMutation({
    mutationFn: async e => {
      return sessionsService.createSession({
        platform_id: 'interfcp_labs',
        agent_id: selectedAgent?.value,
        project_id: 'test',
        mode: selectedAgent?.mode,
      });
    },
  });

  const handleSubmit = async e => {
    if (isSubmitting) return;
    setLoading(true);
    setIsSubmitting(true);
    toggleRef(false);

    try {
      if (createSession) {
        setChatmessages([]);
        const sessionRes = await mutationCreateSession.mutateAsync(e);
        queryClient.invalidateQueries({ queryKey: ['sidebar_sessions'] });

        setSessionID(sessionRes?.session_id);
        updateUserChat(e?.search);
        navigate(`/chat/${sessionRes?.session_id}`);

        form.resetForm();
        setShowEnter(false);
        fakeAiLoader();
        const titleRes = chatService.generateTitle({
          session_id: sessionRes?.session_id,
          query: e?.search,
        });
        const aiResp = await chatService.createChat({
          platform_id: 'interfcp_labs',
          agent_id: selectedAgent?.value,
          session_id: sessionRes?.session_id,
          mode: selectedAgent?.mode,
          query: e?.search,
          use_google_search: webSearch,
        });

        setLoading(false);

        const reader = aiResp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // Helper to extract complete JSON objects from buffer
        function extractJsonObjects(buffer) {
          const objects = [];
          let depth = 0;
          let start = 0;

          for (let i = 0; i < buffer.length; i++) {
            if (buffer[i] === '{') {
              if (depth === 0) start = i;
              depth++;
            } else if (buffer[i] === '}') {
              depth--;
              if (depth === 0) {
                objects.push(buffer.slice(start, i + 1));
              }
            }
          }

          const last = objects.length ? buffer.slice(buffer.lastIndexOf('}') + 1) : buffer;
          return { objects, leftover: last };
        }

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const { objects, leftover } = extractJsonObjects(buffer);

          for (let jsonString of objects) {
            try {
              const parsed = JSON.parse(jsonString);
              const { type, data } = parsed;

              if (type === 'intermediate_step') {
                updateChatLoaderMessage(data); // 🚀 Instant update
                updateBotChat('\n');
              }

              if (type === 'content') updateBotChat(data);
              if (type === 'tool_image_result') updateToolImages(data);
              if (type === 'tool_result') {
                const fixed = parsePossiblyBrokenJson(data);
                updateChatSource(fixed?.search_results);
              }
            } catch (err) {
              console.warn('Failed to parse chunk:', jsonString, err);
            }
          }

          buffer = leftover; // Keep incomplete JSON for next chunk
        }

        // Handle final leftover
        if (buffer.trim()) {
          try {
            const parsed = JSON.parse(buffer);
            const { type, data } = parsed;

            if (type === 'intermediate_step') updateChatLoaderMessage(data);
            if (type === 'content') updateBotChat(data);
            if (type === 'tool_image_result') updateToolImages(data);
            if (type === 'tool_result') {
              const fixed = parsePossiblyBrokenJson(data);
              updateChatSource(fixed?.search_results);
            }
          } catch (err) {
            console.warn('Failed final parse:', buffer, err);
          }
        }

        // Clear loader after stream completion
        updateChatLoaderMessage('');
      } else {
        setLoading(true);
        updateUserChat(e?.search);
        fakeAiLoader();
        form.resetForm();
        setShowEnter(false);

        const aiResp = await chatService.createChat({
          platform_id: 'interfcp_labs',
          agent_id: selectedAgent?.value,
          session_id: sessionId ?? sessionID,
          query: e?.search,
          mode: selectedAgent?.mode,
          use_google_search: webSearch,
        });

        setLoading(false);

        const reader = aiResp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // Helper to extract complete JSON objects from buffer
        function extractJsonObjects(buffer) {
          const objects = [];
          let depth = 0;
          let start = 0;

          for (let i = 0; i < buffer.length; i++) {
            if (buffer[i] === '{') {
              if (depth === 0) start = i;
              depth++;
            } else if (buffer[i] === '}') {
              depth--;
              if (depth === 0) {
                objects.push(buffer.slice(start, i + 1));
              }
            }
          }

          const last = objects.length ? buffer.slice(buffer.lastIndexOf('}') + 1) : buffer;
          return { objects, leftover: last };
        }

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const { objects, leftover } = extractJsonObjects(buffer);

          for (let jsonString of objects) {
            try {
              const parsed = JSON.parse(jsonString);
              const { type, data } = parsed;

              if (type === 'intermediate_step') {
                updateChatLoaderMessage(data); // ✅ Instant update
                updateBotChat('\n \n');
              }

              if (type === 'content') updateBotChat(data);
              if (type === 'tool_image_result') updateToolImages(data);
              if (type === 'tool_result') {
                const fixed = parsePossiblyBrokenJson(data);
                updateChatSource(fixed?.search_results);
              }
            } catch (err) {
              console.warn('Failed to parse chunk:', jsonString, err);
            }
          }

          buffer = leftover; // Keep incomplete JSON for next chunk
        }

        // ✅ Final leftover
        if (buffer.trim()) {
          try {
            const parsed = JSON.parse(buffer);
            const { type, data } = parsed;

            if (type === 'intermediate_step') updateChatLoaderMessage(data);
            if (type === 'content') updateBotChat(data);
            if (type === 'tool_image_result') updateToolImages(data);
            if (type === 'tool_result') {
              const fixed = parsePossiblyBrokenJson(data);
              updateChatSource(fixed?.search_results);
            }
          } catch (err) {
            console.warn('Failed final parse:', buffer, err);
          }
        }

        // ✅ Clear loader after stream ends
        updateChatLoaderMessage('');
      }
    } catch (error) {
      fakeAiLoaderClear();
      setLoading(false);
    } finally {
      setIsSubmitting(false);
    }
  };
  const getAllAgentData = useQuery({
    queryKey: ['get-all-agents'],
    queryFn: () => agentService.getAllAgent(),
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const changeChatModel = async () => {
    setAutoOpen(prev => !prev);
    setWebOpen(false);
  };

  // function base64ToSrc(base64String, mimeType = 'image/png') {
  //   if (!base64String) return null;
  //   return `data:${mimeType};base64,${base64String}`;
  // }

  function base64ToSrcConvert(base64String, mimeType = 'image/png') {
    if (selectedAgent?.mode === 'team') {
      return selectedAgent?.icon;
    } else {
      if (!base64String?.icon) return null;

      if (base64String.icon.startsWith('http://') || base64String.icon.startsWith('https://')) {
        return base64String.icon;
      }

      return `data:${mimeType};base64,${base64String.icon}`;
    }
  }

  return (
    <SearchBlockStyled noMargin={noMargin}>
      <Form
        form={form}
        onSubmit={handleSubmit}
        onChange={e => {
          const value = e.target.value;
          setShowEnter(value.trim().length > 0);
        }}>
        <Form.Item className="main-search" name="search" type="search" placeholder="How can I help you?" noMargin>
          <Field />
        </Form.Item>
      </Form>
      <SearchItem>
        <div className="wrap">
          {/* Auto Dropdown */}
          {!sessionId && (
            <div
              ref={autoRef}
              className={`item-box ${autoOpen ? 'active' : ''} ${autoDirection === 'up' ? 'open-up' : 'open-down'}`}>
              <button type="button" className="btn auto-btn" onClick={changeChatModel}>
                <img src={base64ToSrcConvert(selectedAgent) ?? ferdyIcon} width="24" alt="star" />
                <span className="text">{selectedAgent?.label}</span>
              </button>

              <div ref={autoDropdownRef} className="auto-dropdown">
                <ul className="list">
                  <li
                    onClick={() => {
                      setSelectedAgent({
                        label: 'Jose',
                        value: 'ea6da159-7ecf-4e5b-a5bf-97b2990cc33c',
                        icon: 'https://feedalabs-agent-icon.s3.eu-north-1.amazonaws.com/jose_8a09cca9840b4658b0fc24e246525d47.jpg',
                      });
                      setSessionAgent({
                        label: 'Jose',
                        value: 'ea6da159-7ecf-4e5b-a5bf-97b2990cc33c',
                        icon: 'https://feedalabs-agent-icon.s3.eu-north-1.amazonaws.com/jose_8a09cca9840b4658b0fc24e246525d47.jpg',
                      });
                    }}>
                    <div className="head">
                      <div className="icon">
                        <img
                          src="https://feedalabs-agent-icon.s3.eu-north-1.amazonaws.com/jose_8a09cca9840b4658b0fc24e246525d47.jpg"
                          width="20"
                          alt="star"
                        />
                      </div>
                      <span className="title">Jose</span>
                      <div className="tag">
                        <span className="text">Pro</span>
                      </div>
                    </div>
                    <p>AI that adapts - quick answers or advanced research and computation as needed.</p>
                  </li>
                </ul>
                <div className="agent-list-wrapper">
                  {getAllAgentData?.isPending && <Loaders buttonLoader />}

                  <span className="subtitle">Agents</span>

                  {!getAllAgentData?.isPending &&
                    getAllAgentData?.data?.agents?.map((elem, i) => {
                      return (
                        <div
                          className={`custom-agent ${selectedAgent?.value === elem?.id ? 'active' : ''}`}
                          key={i}
                          onClick={() => {
                            updateAgent({ label: elem?.agent_name, value: elem?.id, icon: elem?.icon });
                            setSessionAgent({ label: elem?.agent_name, value: elem?.id, icon: elem?.icon });
                            setAutoOpen(false);
                          }}>
                          <div className="icon-box">
                            <img src={elem?.icon ?? iconImg2} alt="agent-icon" />
                          </div>
                          <div className="text-wrap">
                            <strong className="title">{elem?.agent_name}</strong>
                          </div>
                        </div>
                      );
                    })}
                </div>

                <span className="subtitle">Teams</span>
                <div className="agent-list-wrapper">
                  {_loading && <Loaders buttonLoader />}

                  {!_loading &&
                    teamData?.map((elem, i) => {
                      return (
                        <div
                          className={`custom-agent ${selectedAgent?.value === elem?.team_id ? 'active' : ''}`}
                          key={i}
                          onClick={() => {
                            updateAgent({
                              label: elem?.team_name,
                              value: elem?.team_id,
                              mode: 'team',
                              icon: elem?.icon,
                            });
                            setSessionAgent({
                              label: elem?.team_name,
                              value: elem?.team_id,
                              icon: elem?.icon,
                              mode: 'team',
                            });
                            setAutoOpen(false);
                          }}>
                          <div className="icon-box">
                            <img src={elem?.icon ?? iconImg2} alt="team-icon" />
                          </div>
                          <div className="text-wrap">
                            <strong className="title">{elem?.team_name}</strong>
                          </div>
                        </div>
                      );
                    })}
                </div>

                <div className="recent-search">
                  <span className="subtitle">Recents</span>
                  <div className="recent-search-box">
                    <div className="icon-box">
                      <img src={iconImg1} width="15" alt="img" />
                    </div>
                    <span className="title">Research</span>
                    <GoArrowUpRight className="arrow" />
                  </div>
                  <div className="recent-search-box">
                    <div className="icon-box">
                      <img src={iconImg2} width="15" alt="img" />
                    </div>
                    <span className="title">Compute</span>
                    <GoArrowUpRight className="arrow" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Web Dropdown */}
          <div
            ref={webRef}
            className={`item-box ${webOpen ? 'active' : ''} ${webDirection === 'up' ? 'open-up' : 'open-down'}`}>
            <button
              type="button"
              className="btn btn-web"
              onClick={() => {
                setWebOpen(prev => !prev);
                setAutoOpen(false);
              }}>
              <img src={iconSearch} width="22" alt="search" />
              <span className="text">Web</span>
            </button>

            <div ref={webDropdownRef} className="web-dropdown">
              <ul className="list">
                <li>
                  <div className="head">
                    {/* <label className="checkbox-label">
                      <input type="checkbox" />
                      <span className="custom-checkbox"></span>
                    </label> */}
                    <div className={`btn-check ${webSearch ? 'checked' : ''}`} onClick={() => updateWebSearch()} />
                    <div className="icon">
                      <img src={iconStar} width="20" alt="star" />
                    </div>
                    <span className="title">Web Search</span>
                  </div>
                  <p>Search the web for relevant info</p>
                </li>
              </ul>
              <button type="button" className="advance-btn">
                <span className="title">Advances options</span>
                <IoIosArrowForward className="arrow" />
              </button>
            </div>
          </div>

          {/* Attach Button */}
          <div className="item-box">
            <ModalContainer
              noHead
              width="1150"
              btnComponent={({ onClick }) => (
                <button type="button" className="btn" onClick={onClick}>
                  <img src={iconAttach} width="22" alt="attach" />
                  <span className="text">Attach</span>
                </button>
              )}
              content={({ onClose }) => <UploadFileModal onClose={onClose} />}
            />
          </div>
        </div>
        {/* Mic + Enter Button */}
        <div className="mic-btn-wrap">
          <button type="button" className="mic-btn" onClick={isListening ? handleStop : handleStart}>
            <img src={isListening ? microphoneActiveIcon : iconMic} width="22" alt="mic" />
          </button>

          {showEnter && (
            <button type="button" className="enter-btn" disabled={isSubmitting} onClick={form.submit}>
              <img src={iconEnter} alt="enter" />
            </button>
          )}
          {loading && <Loaders buttonLoader />}
        </div>
      </SearchItem>
    </SearchBlockStyled>
  );
};

export default SearchBlock;
